/**
 * Northstar Domain Foundation — reusable orientation page.
 *
 * Route: /northstar/domain/:key
 *
 * Fetches BTC-authored foundation content from the CMS via
 * `GET /api/cms/northstar.domain.<key>` AND — Phase B.4 — the set of
 * governed ecosystem entities associated with this domain via
 * `GET /api/northstar/domain/<key>/ecosystem`.  The two fetches are
 * INDEPENDENT: an ecosystem failure/timeout must never take down the
 * static Pass-1 foundation.
 *
 * Gated by the same activation predicate as the rest of Northstar:
 * unactivated members are redirected away via `useActivation`.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { ArrowLeft, Heart, ShieldCheck } from "lucide-react";
import { useActivation } from "../../../hooks/useActivation";
import { api } from "../../../lib/auth";
import DeepSpaceBackground from "../../../components/northstar/DeepSpaceBackground";

// Orb key → CMS key mapping.  Orb keys come from northstarOrbs.js /
// northstar_module._ORBS; CMS keys are the Pass-1 stable identifiers.
const ORB_TO_CMS = {
  legal: "northstar.domain.legal",
  housing: "northstar.domain.housing",
  immigration: "northstar.domain.immigration",
  retail: "northstar.domain.retail",
  nourishment: "northstar.domain.nourishment",
  wellness: "northstar.domain.wellness",
  trainers: "northstar.domain.personal_trainers",
  coaches: "northstar.domain.coaches",
  therapists: "northstar.domain.therapists",
  aesthetics: "northstar.domain.aesthetics",
};

// Phase 7B Batch 1 + Batch 2 — orb key → guided journey id
// (Wellness = Phase 7A). Domains without a registered journey render
// no CTA.
const ORB_TO_JOURNEY = {
  wellness: "btc-wellness-getting-started",
  legal: "btc-legal-getting-started",
  housing: "btc-housing-getting-started",
  immigration: "btc-immigration-getting-started",
  retail: "btc-retail-getting-started",
  nourishment: "btc-nourishment-getting-started",
  trainers: "btc-personal_trainers-getting-started",
  coaches: "btc-coaches-getting-started",
  therapists: "btc-therapists-getting-started",
  aesthetics: "btc-aesthetics-getting-started",
};

function Section({ title, testid, children }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <h2 className="text-[11px] uppercase tracking-[0.28em] text-[#7ED493] mb-3">
        {title}
      </h2>
      <div className="text-white/85 text-base leading-relaxed">{children}</div>
    </section>
  );
}

function ConnectCTA({ entityId, state, onConnect }) {
  const disabled = state === "pending" || state === "requested";
  const label =
    state === "pending"
      ? "Requesting…"
      : state === "requested"
      ? "Requested"
      : state === "error"
      ? "Try again"
      : "Ask to be connected";
  return (
    <div className="mt-3 flex items-center gap-3">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onConnect(entityId)}
        data-testid={`connect-cta-${entityId}`}
        className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7ED493] ${
          disabled
            ? "border-white/15 bg-white/[0.03] text-white/50 cursor-default"
            : state === "error"
            ? "border-[#FFB0A9]/40 bg-[#FFB0A9]/[0.06] text-[#FFB0A9] hover:bg-[#FFB0A9]/[0.12]"
            : "border-[#7ED493]/45 bg-[#7ED493]/[0.06] text-[#7ED493] hover:bg-[#7ED493]/[0.12]"
        }`}
      >
        {label}
      </button>
      {state === "requested" && (
        <span
          className="text-[11px] text-white/55"
          data-testid={`connect-help-${entityId}`}
        >
          BTC will follow up with you.
        </span>
      )}
    </div>
  );
}

function ResourceCard({ item, kind, vetted, connectState, onConnect }) {
  const inner = (
    <div className="rounded-xl border border-white/12 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#7ED493]/40 transition-colors p-4 relative">
      {vetted && (
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#7ED493]"
          data-testid="domain-ecosystem-vetted-chip"
          title="Vetted by BTC — passed BTC's ecosystem review and was approved for patient visibility. Not a medical endorsement."
        >
          <ShieldCheck className="w-3 h-3" strokeWidth={2} />
          Vetted by BTC
        </span>
      )}
      <p className="text-white text-base font-medium mb-1 pr-24">{item.title}</p>
      {item.description ? (
        <p className="text-white/70 text-sm leading-relaxed">{item.description}</p>
      ) : null}
      {vetted && onConnect && (
        <ConnectCTA entityId={item.id} state={connectState} onConnect={onConnect} />
      )}
    </div>
  );
  if (item.route) {
    return (
      <Link
        to={item.route}
        data-testid={`domain-currently-available-${kind || "item"}`}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7ED493] rounded-xl"
      >
        {inner}
      </Link>
    );
  }
  return <div data-testid={vetted ? "domain-ecosystem-item" : "domain-currently-available-item"}>{inner}</div>;
}

/**
 * Explicit patient-safe view model.  We deliberately do NOT spread
 * the server response into JSX — only the enumerated fields below
 * are rendered.  Any unexpected field is ignored.  An entity missing
 * `id` or `canonical_name` is skipped rather than crashing the page.
 */
function normalizeEcosystemItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (typeof raw.id !== "string" || !raw.id) return null;
  if (typeof raw.canonical_name !== "string" || !raw.canonical_name) return null;
  return {
    id: raw.id,
    title: raw.canonical_name,
    description: typeof raw.public_description === "string" ? raw.public_description : null,
    // Provenance is a locked semantic marker: the server sets this
    // to true ONLY when the entity passed the two-part patient
    // visibility filter.  Presence of the field is not enough —
    // require the boolean.
    vetted: raw.vetted_by_btc === true,
  };
}

function NotCurrentlyAvailableList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li
          key={i}
          className="rounded-lg border border-white/8 bg-white/[0.02] p-3"
          data-testid="domain-not-available-item"
        >
          <p className="text-white/75 text-sm font-medium">{it.title}</p>
          {it.note ? (
            <p className="text-white/55 text-sm mt-0.5">{it.note}</p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export default function DomainFoundation() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { data: activation, loading: activationLoading } = useActivation();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // B.4: independent ecosystem fetch.  Never blocks Pass-1 render.
  // `staleTime: 0` semantics are trivially satisfied here — we use
  // raw axios rather than React Query so every mount refetches.
  const [ecosystem, setEcosystem] = useState([]);
  // Phase 8 CBS · deferred enhancement — per-entity connection state
  // keyed by entity_id: 'idle' | 'pending' | 'requested' | 'error'.
  const [connectStates, setConnectStates] = useState({});

  async function handleConnect(entityId) {
    setConnectStates((s) => ({ ...s, [entityId]: "pending" }));
    try {
      await api.post("/northstar/connections", { entity_id: entityId });
      setConnectStates((s) => ({ ...s, [entityId]: "requested" }));
    } catch (e) {
      const status = e?.response?.status;
      // 409 = duplicate active request — patient already asked.
      if (status === 409) {
        setConnectStates((s) => ({ ...s, [entityId]: "requested" }));
      } else {
        setConnectStates((s) => ({ ...s, [entityId]: "error" }));
      }
    }
  }

  // Prime already-requested state from the patient's existing
  // connections so the CTA reflects reality on mount.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/northstar/connections")
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data?.connections) ? r.data.connections : [];
        const primed = {};
        for (const c of rows) {
          if (c.status === "REQUESTED" && typeof c.entity_id === "string") {
            primed[c.entity_id] = "requested";
          }
        }
        setConnectStates((s) => ({ ...primed, ...s }));
      })
      .catch(() => {
        /* silent — CTA still works, patient just re-clicks. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const cmsKey = ORB_TO_CMS[key];

  useEffect(() => {
    if (!cmsKey) {
      setLoading(false);
      setError("unknown_domain");
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/cms/${cmsKey}`)
      .then((r) => {
        if (cancelled) return;
        setPayload(r.data?.value || null);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? "not_found" : "load_failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cmsKey]);

  // Independent ecosystem fetch.  3-second hard timeout with silent
  // fallback to Pass-1 CMS content — the patient never sees an
  // infrastructure error banner for the ecosystem layer.
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 3000);
    api
      .get(`/northstar/domain/${key}/ecosystem`, { signal: controller.signal })
      .then((r) => {
        if (cancelled) return;
        const raw = Array.isArray(r.data?.items) ? r.data.items : [];
        setEcosystem(raw.map(normalizeEcosystemItem).filter(Boolean));
      })
      .catch(() => {
        if (cancelled) return;
        setEcosystem([]);
        if (process.env.NODE_ENV !== "production") {
          // Operator-visible only; never surfaced to patients.
          // eslint-disable-next-line no-console
          console.warn(`[B.4] Ecosystem fetch failed for domain=${key}; falling back to CMS.`);
        }
      })
      .finally(() => clearTimeout(t));
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(t);
    };
  }, [key]);

  if (activationLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#040816]"
        data-testid="domain-foundation-loading"
      >
        <Heart className="w-8 h-8 text-[#7ED493] animate-pulse" />
      </div>
    );
  }
  if (!activation) return <Navigate to="/portal" replace />;
  if (!activation.northstar_access_granted) {
    if (activation.should_render_holding_view)
      return <Navigate to="/portal/holding" replace />;
    return <Navigate to="/portal" replace />;
  }

  if (!cmsKey) {
    return (
      <div
        className="min-h-screen text-white bg-[#040816] flex items-center justify-center p-6"
        data-testid="domain-foundation-unknown"
      >
        <div className="max-w-md text-center">
          <p className="text-white/70 mb-4">This area of Northstar wasn't found.</p>
          <button
            onClick={() => navigate("/northstar")}
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#7ED493] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" /> Return to Northstar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden bg-[#040816]"
      data-testid="domain-foundation"
      data-domain-key={key}
    >
      <DeepSpaceBackground />

      <header
        className="relative z-20 px-4 sm:px-6 pt-4 sm:pt-5 pb-2"
        data-testid="domain-foundation-header"
      >
        <div className="flex items-center justify-between gap-4 max-w-3xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#7ED493]">
            Northstar
          </p>
          <Link
            to="/northstar"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
            data-testid="domain-foundation-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Northstar
          </Link>
        </div>
      </header>

      <main className="relative z-10 px-4 sm:px-6 pb-24">
        <div className="max-w-3xl mx-auto pt-6 sm:pt-10">
          {loading && (
            <div
              className="py-24 flex items-center justify-center"
              data-testid="domain-foundation-fetching"
            >
              <Heart className="w-6 h-6 text-[#7ED493] animate-pulse" />
            </div>
          )}

          {!loading && error === "not_found" && (
            <div
              className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-white/75"
              data-testid="domain-foundation-empty"
            >
              This part of your Northstar is being prepared.
            </div>
          )}

          {!loading && error === "load_failed" && (
            <div
              className="rounded-xl border border-[#FFB0A9]/30 bg-[#FFB0A9]/[0.05] p-6 text-white/80"
              data-testid="domain-foundation-error"
            >
              We couldn't load this area just now. Please try again in a moment.
            </div>
          )}

          {!loading && !error && payload && (
            <>
              {/* Title */}
              <h1
                className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-6"
                data-testid="domain-foundation-title"
              >
                {payload.title || payload.label}
              </h1>

              {/* Intro */}
              {payload.intro && (
                <p
                  className="text-white/85 text-base sm:text-lg leading-relaxed mb-10"
                  data-testid="domain-foundation-intro"
                >
                  {payload.intro}
                </p>
              )}

              {/* Phase 7B — Guided Journey CTA (only for domains that
                 ship a registered journey; renders inert if unwired). */}
              {ORB_TO_JOURNEY[key] && (
                <Link
                  to={`/northstar/journeys/${ORB_TO_JOURNEY[key]}`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#7ED493] text-black px-5 py-2.5 text-sm font-medium mb-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7ED493]"
                  data-testid="domain-guided-journey-cta"
                >
                  Start guided journey
                </Link>
              )}

              {/* What belongs here */}
              {payload.what_belongs_here && (
                <Section title="What belongs here" testid="domain-foundation-what-belongs-here">
                  <p>{payload.what_belongs_here}</p>
                </Section>
              )}

              {/* Currently available — B.4: ecosystem entries render
                 FIRST inside the existing "Available now" slot, then
                 Pass-1 CMS items.  Boundary remains last on the page
                 (enforced by JSX order, not CSS). */}
              {(ecosystem.length > 0 || (Array.isArray(payload.currently_available) && payload.currently_available.length > 0)) ? (
                <Section title="Available now" testid="domain-foundation-currently-available">
                  <div className="grid gap-3" data-testid="domain-foundation-available-list">
                    {ecosystem.map((it) => (
                      <ResourceCard
                        key={`eco-${it.id}`}
                        item={it}
                        vetted
                        connectState={connectStates[it.id] || "idle"}
                        onConnect={handleConnect}
                      />
                    ))}
                    {Array.isArray(payload.currently_available) && payload.currently_available.map((it, i) => (
                      <ResourceCard key={`cms-${i}`} item={it} kind={it.kind} />
                    ))}
                  </div>
                </Section>
              ) : (
                payload.growing_message && (
                  <Section title="Available now" testid="domain-foundation-growing">
                    <p className="text-white/75 italic">{payload.growing_message}</p>
                  </Section>
                )
              )}

              {/* In development */}
              {Array.isArray(payload.in_development) && payload.in_development.length > 0 && (
                <Section title="In development" testid="domain-foundation-in-development">
                  <ul className="space-y-3">
                    {payload.in_development.map((it, i) => (
                      <li
                        key={i}
                        className="rounded-lg border border-[#7ED493]/25 bg-[#7ED493]/[0.05] p-3"
                      >
                        <p className="text-white text-sm font-medium">{it.title}</p>
                        {it.description ? (
                          <p className="text-white/70 text-sm mt-0.5">{it.description}</p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Not currently available */}
              {Array.isArray(payload.not_currently_available) && payload.not_currently_available.length > 0 && (
                <Section
                  title="Not currently available"
                  testid="domain-foundation-not-available"
                >
                  <NotCurrentlyAvailableList items={payload.not_currently_available} />
                </Section>
              )}

              {/* Future */}
              {Array.isArray(payload.future) && payload.future.length > 0 && (
                <Section title="Looking ahead" testid="domain-foundation-future">
                  <ul className="space-y-2 text-white/75">
                    {payload.future.map((it, i) => (
                      <li key={i} className="text-sm">
                        <span className="text-white/90">{it.title}</span>
                        {it.description ? (
                          <span className="text-white/60"> — {it.description}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Boundary — always last */}
              {payload.boundary && (
                <section
                  className="mt-8 rounded-xl border border-white/10 bg-white/[0.02] p-5"
                  data-testid="domain-foundation-boundary"
                >
                  <p className="text-[11px] uppercase tracking-[0.28em] text-white/50 mb-2">
                    A note on scope
                  </p>
                  <p className="text-white/75 text-sm leading-relaxed">
                    {payload.boundary}
                  </p>
                </section>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
